import { useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AxiosError, AxiosInstance } from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { Input, PageHeadline } from '../../../components/atoms';
import { CustomCheckbox, CustomInput, CustomInputNumber, InputGroup } from '../../../components/inputs';
import { OnboardingHeader } from '../../../components/OnboardingHeader';

import { Stepper } from '../../../components/Stepper';
import { useStepperContext } from '../../../components/Stepper/Context';

import { CompanyRegisteringService } from '../../../services/CompanyRegisteringService';

import { hasFileName, validateFileSize, validateFileType } from '../../../utils/fileValidation';

import { useAppSelector } from '../../../hooks/hooksRedux';
import { RootState } from '../../../store';
import { useAuth } from '../../../store/auth';

import { errorToast } from '../../../utils/toasts';
import { errorMessageAdapter } from '../../../adapters';
import { AccountService } from '../../../services/AccountService';
import { useApi } from '../../../services/hooks/apiHook';
import { IJurisdictions } from '../../../services/Interfaces/IAccountService';
import { ICurrency } from '../../../services/Interfaces/ICompanyRegisteringService';
import { StyledLabel, StyledSelect } from './styleGuide';
import { defaultBorder, defaultButton, defaultColors } from '../../../styles/style-guide';
import { CustomInputNumberFormat } from '../../../components/inputs/CustomInputNumberFormat/CustomInputNumberFormat';
import { useTranslation } from 'react-i18next';
import { FiUploadCloud } from 'react-icons/fi';
import { ButtonBack, ButtonNext } from '../../DashboardCustomer/OnboardingOffshore/StyleGuide';
import { randomInt } from '../../../utils/random';
import { TRANSFER_STEP_PENDING_KEY, TRANSFER_STEP_SESSION_KEY } from './constants';

enum TypeCurrency {
  USD = '1',
  EUR = '2',
  GBP = '3',
  SGD = '4'
}

interface CompanyTransferFormSchema {
  company_name: string;
  register_number: string;
  jurisdiction_id: string;
  incorporation_file: File | undefined;
  is_retrieve_documents: string | undefined;
  number_of_director?: string | undefined;
  number_of_shareholder?: string | undefined;
  total_authorized_shares?: string;
  par_value?: string;
  total_issued_shares?: string;
  currency?: string;
}

const getUSDCurrencySlug = (items: ICurrency[] = []): string => {
  const currency = items.find((item) => item.acronym === 'USD');

  return String(currency?.id || '');
};

export const CompanyTransferData = () => {
  const [fileUploadDocument, setFileUploadDocument] = useState<string | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [jurisdictions, setJurisdictions] = useState<IJurisdictions[]>([]);
  const [jurisdictionStructureId, setJurisdictionStructureId] = useState<string>('');
  const [totalAuthorizedSharesControled, setTotalAuthorizedSharesControled] = useState<number>(50000);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const stepperContext = useStepperContext();

  const { legalPersonalitySlug } = useAppSelector((state: RootState) => state.language);
  const { colors, border, button } = useAppSelector((state: RootState) => state.styleGuide.data);

  const primaryColor = colors.primary || defaultColors.primary;
  const primaryBorderColor = border.primary || defaultBorder.primary;
  const focusBorderColor = border.hover || defaultBorder.hover;
  const primaryButtonColor = button.primary || defaultButton.primary;
  const thirdaryColor = colors.white || defaultColors.white;
  const randomImage = useMemo(() => randomInt(1, 5), []);

  const { token, setToken } = useAuth();
  const api: AxiosInstance = useApi();

  const formatCurrencyType = (valueCurrency: string): string => {
    switch (valueCurrency) {
      case TypeCurrency.USD:
        return 'US$';
      case TypeCurrency.EUR:
        return '€';
      case TypeCurrency.GBP:
        return '£';
      case TypeCurrency.SGD:
        return '$';
      default:
        return 'US';
    }
  };

  const validationSchema = Yup.object().shape({
    company_name: Yup.string().required(t('validations.valRequired') || ''),
    register_number: Yup.string().required(t('validations.valRequired') || ''),
    jurisdiction_id: Yup.string().required(t('validations.valRequired') || ''),
    currency: Yup.string(),
    incorporation_file: Yup.mixed()
      .test('required', t('Required') || '', hasFileName)
      .test('fileSize', t('OnboardingCreateAccountErrorFileSize') || '', validateFileSize)
      .test('fileType', t('OnboardingCreateAccountErrorFileType') || '', validateFileType),
    number_of_director: Yup.number()
      .min(1, t('validations.valMustZero') || '')
      .required(t('validations.valMustbeNumber') || ''),
    number_of_shareholder: Yup.number()
      .min(1, t('validations.valMustZero') || '')
      .required(t('validations.valMustbeNumber') || ''),
    total_authorized_shares: Yup.number()
      .transform((value, originalValue) => {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        if (originalValue === '' || originalValue == null) return undefined;
        const numericValue = parseFloat(String(originalValue).replace(/,/g, ''));
        return Number.isNaN(numericValue) ? undefined : numericValue;
      })
      .required(t('Required') || '')
      .min(1, t('others.othMinValue') || '')
      .max(50000, `${t('others.othExceededTheShareLimit')}: ${50000}`),
    total_issued_shares: Yup.number()
      .transform((value, originalValue) => {
        if (typeof value === 'number' && !Number.isNaN(value)) return value;
        if (originalValue === '' || originalValue == null) return undefined;
        const numericValue = parseFloat(String(originalValue).replace(/,/g, ''));
        return Number.isNaN(numericValue) ? undefined : numericValue;
      })
      .required(t('Required') || '')
      .min(1, t('others.othMinValue') || '')
      .max(
        totalAuthorizedSharesControled || 50000,
        `${t('others.othExceededTheShareLimit')}: ${totalAuthorizedSharesControled || 50000}`
      )
  });

  const {
    register,
    setValue,
    getValues,
    watch,
    resetField,
    formState: { errors },
    handleSubmit,
    control
  } = useForm<CompanyTransferFormSchema>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      company_name: '',
      register_number: '',
      jurisdiction_id: '',
      incorporation_file: undefined,
      is_retrieve_documents: '0',
      number_of_director: '0',
      number_of_shareholder: '0',
      total_authorized_shares: '',
      par_value: '',
      total_issued_shares: '',
      currency: ''
    },
    mode: 'onSubmit',
    shouldUnregister: false
  });

  const currenteCurrency = watch('currency') || '';
  const totalAuthorizedShares = watch('total_authorized_shares');
  const isRetrueveDocuments: any = watch('is_retrieve_documents');
  const formatCurrency = formatCurrencyType(currenteCurrency);

  const headers = { 'Content-Language': legalPersonalitySlug, Authorization: `Bearer ${token}` };

  // Resolve structure for the selected jurisdiction. Used on select and as submit fallback
  // when onChange never ran / race with a slow API left jurisdictionStructureId empty.
  const resolveJurisdictionStructureId = async (jurisdictionId: string): Promise<string | null> => {
    if (!jurisdictionId) return null;

    try {
      const { data } = await AccountService.getStructureBasedJurisdiction(Number(jurisdictionId), api, { headers });
      const structureId = data?.[0]?.id ? String(data[0].id) : null;
      if (structureId) {
        setJurisdictionStructureId(structureId);
      }
      return structureId;
    } catch (error) {
      console.error('Fetch jurisdiction structures error', error);
      errorToast(errorMessageAdapter(error as AxiosError));
      return null;
    }
  };

  const setNewTokenWithCompanyResponse = async (companyId: number) => {
    const payload = { company_id: companyId };
    const { response } = await AccountService.changeCompanySelected(api, payload, { headers });
    setToken(response.access_token);
  };

  const onSubmitForm: SubmitHandler<CompanyTransferFormSchema> = async (data) => {
    const watchValueIsRetrieve = getValues('is_retrieve_documents');
    try {
      setLoading(true);

      let structureId = jurisdictionStructureId;
      if (!structureId) {
        structureId = (await resolveJurisdictionStructureId(data.jurisdiction_id)) || '';
      }

      if (!structureId) {
        setLoading(false);
        errorToast(t('validations.valRequiredJurisdictionStructure') || t('validations.valRequired') || '');
        return;
      }

      let payloadSend: Record<string, unknown> = {};

      if (!watchValueIsRetrieve || watchValueIsRetrieve === '0') {
        const { currency: _, ...newData } = data;
        payloadSend = {
          ...newData,
          is_retrieve_documents: '0',
          is_corporate_director: '0',
          underages: '0',
          par_value: parseInt(String(data.par_value || '0').replace(/,/g, ''), 10),
          legal_personality_id: '1',
          is_accounting_module: 0,
          jurisdiction_structure_id: structureId
        };
      } else {
        const { currency, number_of_director, par_value, number_of_shareholder, ...newData } = data;
        payloadSend = {
          ...newData,
          is_retrieve_documents: '1',
          legal_personality_id: '1',
          is_accounting_module: 0,
          par_value: parseInt(String(par_value || '0').replace(/,/g, ''), 10),
          jurisdiction_structure_id: structureId
        };
      }

      const response = await CompanyRegisteringService.registerCompanyTransfer(api, payloadSend, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.success === true) {
        // Delaware: API seta progress_step=transfer_shareholders e number_of_director=0.
        // Bahamas/outros: transfer_directors. AuthGuard remonta no setToken — persistir antes.
        const selectedJurisdiction = jurisdictions.find(
          (jurisdiction) => String(jurisdiction.id) === String(data.jurisdiction_id)
        );
        const nextStepId =
          selectedJurisdiction?.description === 'Delaware' ? 'transfer_shareholders' : 'transfer_directors';

        sessionStorage.setItem(TRANSFER_STEP_PENDING_KEY, nextStepId);
        sessionStorage.setItem(TRANSFER_STEP_SESSION_KEY, nextStepId);
        await setNewTokenWithCompanyResponse(response.data.id);
        // Remount restaura via getInitialStep(); nextStep é best-effort se ainda montado.
        if (nextStepId === 'transfer_directors') {
          stepperContext.nextStep();
        } else {
          stepperContext.goToStep('transfer_shareholders');
        }
      } else {
        errorToast(t('validations.valRequired') || '');
      }
    } catch (e: any) {
      errorToast(errorMessageAdapter(e as AxiosError));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectJurisdiction = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    if (!selectedValue) return;

    setValue('jurisdiction_id', selectedValue);
    setJurisdictionStructureId('');
    await resolveJurisdictionStructureId(selectedValue);
  };

  const UploadCard = ({
    label,
    inputId,
    fileName,
    errorMessage,
    onFileSelect
  }: {
    label: string;
    inputId: string;
    fileName?: string | null;
    errorMessage?: string;
    onFileSelect: (file?: File) => void;
  }) => (
    <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{label}</p>
          <p className="text-xs text-slate-500">{t('labels.lblDocumentsSupported')}</p>
        </div>
        {fileName ? (
          <span className="max-w-[180px] truncate rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-700">
            {fileName}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">PDF/DOCX</span>
        )}
      </div>
      <label className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 transition hover:border-blue-300 hover:bg-blue-50/40">
        <div className="flex items-center gap-3 text-slate-600">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-blue-600">
            <FiUploadCloud size={18} />
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-700">{t('labels.lblPushYourDocument')}</span>
            <span className="text-xs text-slate-500">{t('labels.lblOrSearchInYourComputer')}</span>
          </div>
        </div>
        <span className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white">
          {t('labels.lblPushYourDocument')}
        </span>
        <input
          id={inputId}
          type="file"
          className="hidden"
          accept="image/*,.pdf"
          {...register('incorporation_file')}
          onChange={(e) => onFileSelect(e.target.files?.[0])}
        />
      </label>
      {errorMessage ? <span className="text-xs text-red-500">{errorMessage}</span> : null}
    </div>
  );

  const handleCheckboxChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget;
    if (checked) {
      setIsChecked(true);
      setValue('is_retrieve_documents', '1');
    } else {
      setIsChecked(false);
      setValue('is_retrieve_documents', '0');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [currenciesResponse, jurisdictionsResponse] = await Promise.all([
          CompanyRegisteringService.listCurrencies(api, { headers }),
          AccountService.getJurisdictions(api, { headers })
        ]);

        if (currenciesResponse) setCurrencies(currenciesResponse.data);
        if (jurisdictionsResponse?.data) setJurisdictions(jurisdictionsResponse.data);
      } catch (error) {
        console.error('Fetch company transfer options error', error);
        errorToast(errorMessageAdapter(error as AxiosError));
      }
    })();
  }, []);

  useEffect(() => {
    if (isRetrueveDocuments === '1') {
      setValue('number_of_director', '1');
      setValue('number_of_shareholder', '1');
      setValue('par_value', '0');
      setValue('total_authorized_shares', '1');
      setValue('total_issued_shares', '1');
      return;
    }

    resetField('number_of_director');
    resetField('number_of_shareholder');
    resetField('total_authorized_shares');
    resetField('total_issued_shares');
  }, [isRetrueveDocuments]);

  useEffect(() => {
    if (totalAuthorizedShares !== '') {
      setValue('currency', getUSDCurrencySlug(currencies));
      setValue('par_value', totalAuthorizedShares);
      setTotalAuthorizedSharesControled(Number(totalAuthorizedShares));
    }
  }, [currencies, totalAuthorizedShares]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 lg:min-h-screen">
      <div className="w-full box-border lg:pr-[20vw]">
        <div className="px-6 py-8 lg:px-12">
          <div className="mx-auto w-full max-w-5xl">
          <OnboardingHeader showLogoutButton />

          <div className="mt-6">
            <PageHeadline
              title={t('companyTransfer.othTllCompanyRegistering')}
              className="mt-8 shadow-xl"
              gradientFrom="from-primary"
              gradientTo="to-blue-800"
            />
          </div>

          <div className="mt-6">
            <Stepper currentStep={2} maxSteps={5} />
          </div>

          <div className="mt-8 rounded-xl bg-white p-8 shadow-sm ring-1 ring-slate-900/5">
            <form id="form-company-transfer" onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center justify-between gap-[40px] md:flex-row">
                  <CustomInput
                    id="company_name"
                    label={t('companyTransfer.othCompanyName') || ''}
                    error={errors.company_name?.message}
                    register={register}
                  />
                </div>
              <div className="flex flex-col items-center justify-between gap-[40px] md:flex-row">
                <CustomInput
                  id="register_number"
                  label={t('companyTransfer.othNumberRegisterCompany') || ''}
                  error={errors.register_number?.message}
                  register={register}
                />
              </div>

              <div className="flex flex-col items-center justify-between gap-[40px] md:flex-row">
                <div className="flex w-full flex-col gap-1">
                  <div className="flex flex-row items-center gap-3">
                    <StyledLabel hasError={!!errors.jurisdiction_id?.message} $colors={primaryColor}>
                      {t('companyTransfer.othJuridiction') || ''}
                    </StyledLabel>
                  </div>
                  <StyledSelect
                    id="jurisdiction_id"
                    hasError={!!errors.jurisdiction_id?.message}
                    $borderColors={primaryBorderColor}
                    $focusColor={focusBorderColor}
                    {...register('jurisdiction_id')}
                    onChange={(e) => {
                      handleSelectJurisdiction(e);
                    }}
                  >
                    <option value="" disabled>
                      {t('companyTransfer.othSelectJurisdiction') || ''}
                    </option>
                    {jurisdictions.map((jurisdiction) => (
                      <option key={jurisdiction.id} value={jurisdiction.id}>
                        {jurisdiction.description}
                      </option>
                    ))}
                  </StyledSelect>{' '}
                </div>
              </div>
              <Controller
                control={control}
                name={'incorporation_file'}
                render={() => (
                  <UploadCard
                    label={t('companyTransfer.othUpladoDocument') || ''}
                    inputId="incorporation_file"
                    fileName={fileUploadDocument}
                    errorMessage={errors?.incorporation_file?.message}
                    onFileSelect={(file) => {
                      if (file) {
                        setFileUploadDocument(file.name);
                        setValue('incorporation_file', file);
                      }
                    }}
                  />
                )}
              />
              <div className="group relative flex w-full flex-col items-center justify-between gap-6 rounded-lg border border-slate-200 px-4 py-3 shadow-sm md:flex-row">
                <CustomCheckbox
                  id="is_retrieve_documents"
                  name="is_retrieve_documents"
                  label={t('companyTransfer.othCheckCompanyDocument') || ''}
                  register={register}
                  checked={isChecked}
                  onChange={(e) => handleCheckboxChange(e)}
                />
                <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-3 w-[min(320px,90vw)] -translate-x-1/2 translate-y-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
                  <div className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 rounded-sm bg-white shadow-sm" />
                  <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm shadow-lg">
                    <div style={{ color: primaryColor }} className="mb-2 text-sm font-semibold">
                      {t('companyTransfer.othModalTitleInformation')}
                    </div>
                    <div className="text-slate-600">{t('companyTransfer.othModalInformation')}</div>
                  </div>
                </div>
              </div>
              {!isChecked && (
                <div className="">
                  <div className="flex w-full flex-row flex-wrap items-center justify-between pb-4">
                    <div className="flex flex-col flex-wrap pb-4 pr-4">
                      <div className="pl-2">
                        <InputGroup
                          label={t('companyTransfer.othNumberDirectors')}
                          tooltip={t('tooltips.tltDirector') as string}
                        >
                          <CustomInputNumber
                            getValues={getValues}
                            setValue={setValue}
                            id="number_of_director"
                            register={register}
                            error={errors.number_of_director?.message}
                          />
                        </InputGroup>
                      </div>
                    </div>
                    <div className="flex flex-col flex-wrap pb-4">
                      <div className="pl-2">
                        <InputGroup
                          label={t('companyTransfer.othNumberShareholders')}
                          tooltip={t('tooltips.tltShareholder') as string}
                        >
                          <CustomInputNumber
                            getValues={getValues}
                            setValue={setValue}
                            id="number_of_shareholder"
                            register={register}
                            error={errors.number_of_shareholder?.message}
                          />
                        </InputGroup>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center justify-between gap-[40px] pb-4 md:flex-row">
                    <Controller
                      name="total_authorized_shares"
                      control={control}
                      render={({ field }) => (
                        <CustomInputNumberFormat
                          {...field}
                          id="total_authorized_shares"
                          control={control}
                          label={t('companyTransfer.othTotalAuthorizedShares') || ''}
                          error={errors.total_authorized_shares?.message}
                          placeholder="0"
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col items-center justify-between gap-[40px] pb-4 md:flex-row">
                    <Controller
                      name="par_value"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label={t('others.othParValue') || ''}
                          tooltip={t('others.othParValueTooltip') || ''}
                          value={`${formatCurrency} ${!totalAuthorizedShares ? '' : totalAuthorizedShares}`}
                          disabled
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col items-center justify-between gap-[40px] pb-4 md:flex-row">
                    <Controller
                      control={control}
                      name={'total_issued_shares'}
                      defaultValue=""
                      render={({ field }) => (
                        <CustomInput
                          id="total_issued_shares"
                          label={t('companyTransfer.othTotalIssuedShares') || ''}
                          error={errors.total_issued_shares?.message}
                          register={register}
                          {...field}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col items-center justify-between gap-[40px] pb-4 md:flex-row">
                    <div className="flex w-full flex-col gap-1">
                      <div className="flex flex-row items-center gap-3">
                        <StyledLabel hasError={!!errors.currency?.message} $colors={primaryColor}>
                          {t('companyTransfer.othCurrency') || ''}
                        </StyledLabel>
                      </div>
                      <select
                        className={`${
                          errors.currency?.message ? 'border-red-500' : 'border-[#CED4DA]'
                        } h-[40px] w-full rounded-lg border-[1px] bg-white px-4 text-sm outline-none focus:border-blue-700`}
                        {...register('currency')}
                      >
                        <option value="" defaultChecked disabled>
                          {t('others.othSelectCurrency')}
                        </option>
                        {currencies.map((currency, index) => (
                          <option key={`${currency.acronym}${index}`} value={currency.id}>
                            {currency.acronym}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
              </div>
            </form>
          </div>

          <div className="mt-8 flex w-full flex-row justify-center gap-4">
            <ButtonBack
              $borderColor={primaryBorderColor}
              $colorText={primaryButtonColor}
              onClick={() => navigate('/choose-onboarding')}
            >
              {t('buttons.btnBack')}
            </ButtonBack>
            <ButtonNext
              $borderColor={primaryBorderColor}
              $colorText={thirdaryColor}
              $backgroundColor={primaryButtonColor}
              form="form-company-transfer"
              type="submit"
              disabled={loading}
            >
              {t('buttons.btnNext')}
            </ButtonNext>
          </div>
          </div>
        </div>
      </div>

      <div className="fixed right-0 top-0 hidden h-screen lg:block lg:w-1/5">
        <div className="relative h-full">
          <img src={`/img/image0${randomImage}.png`} className="h-full w-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
          <p className="absolute bottom-[25%] left-6 right-6 text-[32px] font-bold leading-tight text-white">
            {t('labels.lblWeveHelped')}
          </p>
        </div>
      </div>
    </div>
  );
};
